import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { client, clientError } from "../../libs/Client";
import { dateComparator } from "../../libs/Utils";
import PageHeader from "../../components/PageHeader";
import AddReferralType from "../../components/SlideOvers/AddReferralType";
import DeleteDialog from "../../components/Dialogs/DeleteDialog";
import EditReferralType from "../../components/SlideOvers/EditReferralType";

export default function Tags() {
  const gridRef = useRef();
  const [referralTypes, setReferralTypes] = useState([]);

  useEffect(() => {
    document.title = "Capital Now Funding - Admin - Referral Types";
  });

  const [columnDefs] = useState([
    { field: "name", filter: true },
    {
      field: "createdAt",
      filter: false,
      comparator: dateComparator,
      headerName: "Created",
      cellRenderer: (params) => {
        return moment(params.data.createdAt).fromNow();
      },
    },
    {
      field: "updatedAt",
      filter: false,
      comparator: dateComparator,
      headerName: "Updated",
      cellRenderer: (params) => {
        return moment(params.data.updatedAt).fromNow();
      },
    },
    {
      field: "id",
      filter: false,
      headerName: "",
      minWidth: 125,
      maxWidth: 125,
      cellRenderer: (params) => {
        return (
          <div className="flex justify-end space-x-4">
            <EditReferralType id={params.value} saved={handleRefresh} />
            <DeleteDialog
              tooltip="Delete Referral Type"
              buttonTitle="Delete"
              title="Delete Referral Type?"
              content="Are you sure you want to delete the referral type?"
              id={params.value}
              idToDelete={handleDelete}
            />
          </div>
        );
      },
    },
  ]);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      suppressMovable: true,
    }),
    [],
  );

  const onGridReady = useCallback(() => {
    client()
      .get(`/referral-types`)
      .then((res) => {
        setReferralTypes(res.data);
      })
      .catch((err) => {
        clientError(err);
      });
  }, []);

  const onFirstDataRendered = useCallback((params) => {
    gridRef.current.api.sizeColumnsToFit();
  }, []);

  function handleDelete(idToDelete) {
    client()
      .delete(`/referral-types/${idToDelete}`)
      .then(() => {
        toast.success("Referral type deleted");
        handleRefresh();
      })
      .catch((err) => {
        clientError(err);
      });
  }

  function handleRefresh() {
    client()
      .get(`/referral-types`)
      .then((res) => {
        setReferralTypes(res.data);
      })
      .catch((err) => {
        clientError(err);
      });
  }

  return (
    <div className="px-4 pt-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <PageHeader title="Tags" subtitle="Referral Types" />
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <AddReferralType saved={handleRefresh} />
        </div>
      </div>
      <div className="mt-8 flex flex-col">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="mb-8 ml-2 mr-2">
            <div className="ag-theme-alpine" style={{ width: "100%", height: 750 }}>
              <AgGridReact
                ref={gridRef}
                rowData={referralTypes}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                animateRows={true}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                suppressCellFocus={true}
              ></AgGridReact>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
